// seo.js - Utilities for SEO optimization
// Created by Claude on 20.05.2025 for SEO optimization

import { API_BASE_URL } from '../config';
import { get } from './api';

/**
 * Generate meta description from HTML content
 * @param {string} htmlContent - HTML content to extract text from
 * @param {number} maxLength - Maximum length of the description
 * @returns {string} Plain text description
 */
export const generateMetaDescription = (htmlContent, maxLength = 160) => {
  if (!htmlContent) return '';
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Get text content and remove extra whitespace
  let text = tempDiv.textContent || tempDiv.innerText || '';
  text = text.replace(/\s+/g, ' ').trim();
  
  // Truncate to maxLength, but try to end at a sentence or word boundary
  if (text.length <= maxLength) return text;
  
  // Try to find a sentence end before maxLength
  let end = text.lastIndexOf('.', maxLength - 3);
  
  // If no sentence end found, try to find a word boundary
  if (end === -1 || end < maxLength * 0.7) {
    end = text.lastIndexOf(' ', maxLength - 3);
  }
  
  // If all else fails, just truncate
  if (end === -1) end = maxLength - 3;
  
  return text.substring(0, end) + '...';
};

/**
 * Generate keywords from content and/or category
 * @param {string} content - Content to extract keywords from
 * @param {string} category - Content category
 * @param {string} defaultKeywords - Default keywords to use if content is insufficient
 * @returns {string} Comma-separated keywords
 */
export const generateKeywords = (content = '', category = '', defaultKeywords = '') => {
  // Define base keywords for each category
  const categoryKeywords = {
    cars: 'drift car, drift spec, drift build, motorsport, racecar',
    events: 'drift event, drift competition, drift show, drift race, drift championship',
    products: 'drift merch, drift gear, drift shop, drift apparel, drift accessories',
    blog: 'drift blog, drift news, drift tips, drift tutorials, drift culture',
    team: 'drift team, drift drivers, professional drifting, drift pilots, drift crew',
    gallery: 'drift photos, drift videos, drift gallery, drift media, drift moments'
  };
  
  // Start with base keywords for the category
  const baseKeywords = categoryKeywords[category.toLowerCase()] || 'drift, drifting, drift team';
  
  // If we have content, try to extract meaningful keywords
  if (content && content.length > 100) {
    // Remove HTML tags to get plain text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Filter for meaningful words (longer than 4 letters, not too common)
    const commonWords = ['this', 'that', 'with', 'from', 'their', 'there', 'have', 'about'];
    const words = text.split(/[\s,\.]+/).filter(word => 
      word.length > 4 && !commonWords.includes(word.toLowerCase())
    );
    
    // Count occurrences
    const wordCounts = {};
    words.forEach(word => {
      const normalizedWord = word.toLowerCase();
      wordCounts[normalizedWord] = (wordCounts[normalizedWord] || 0) + 1;
    });
    
    // Get top keywords (most frequent words)
    const sortedWords = Object.entries(wordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(entry => entry[0]);
    
    if (sortedWords.length > 0) {
      return `${baseKeywords}, ${sortedWords.join(', ')}`;
    }
  }
  
  // Fall back to default or category keywords
  return defaultKeywords || baseKeywords;
};

/**
 * Submit sitemap to search engines
 * @returns {Promise<object>} Submission results
 */
export const submitSitemapToSearchEngines = async () => {
  try {
    // Trigger sitemap generation
    await get('/sitemap/generate-all');
    
    // Submit sitemap URLs to search engines
    const results = await get('/api/seo/submit-sitemap');
    return results;
  } catch (error) {
    console.error('Error submitting sitemap:', error);
    throw error;
  }
};

/**
 * Extracts alt text from an image URL
 * @param {string} imageUrl - Image URL to extract alt text from
 * @returns {string} Alt text for the image
 */
export const getImageAltText = (imageUrl = '') => {
  if (!imageUrl) return '';
  
  try {
    // Extract filename from URL
    const filename = imageUrl.split('/').pop();
    
    // Remove file extension and replace hyphens/underscores with spaces
    let altText = filename
      .replace(/\.\w+$/, '') // Remove file extension
      .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
      .replace(/\d+/g, '') // Remove numbers (like timestamps)
      .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
      .trim();
    
    // Capitalize first letter of each word
    altText = altText
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return altText || 'NO RISK NO DRIFT Drift Team';
  } catch (error) {
    return 'NO RISK NO DRIFT Drift Team';
  }
};

export default {
  generateMetaDescription,
  generateKeywords,
  submitSitemapToSearchEngines,
  getImageAltText
};